import React, {useEffect, useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {
    Typography,
    Box,
    Grid,
    Paper,
    Tabs,
    Tab,
    Chip,
    Avatar,
    CircularProgress,
    IconButton, CardHeader,
} from "@mui/material"
import {useTranslation} from "react-i18next"
import i18n from "../../i18n/i18n"
import axiosInstance from "../../config/axios-config"
import {PageContainer} from "../../components/common/page-container"
import {GetLogo} from "../../profile/main/api/photo-data-handler"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PhoneIcon from "@mui/icons-material/Phone"
import EmailIcon from "@mui/icons-material/Email"
import VerifiedIcon from "@mui/icons-material/Verified"
import StorefrontIcon from "@mui/icons-material/Storefront"
import LanguageIcon from "@mui/icons-material/Language"
import BusinessIcon from "@mui/icons-material/Business"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import {TabOverview} from "./tabs/tab-overview";
import TabAllProducts from "./tabs/tab-all-products";
import {TabDelivery} from "./tabs/tab-delivery";
import {TabPayment} from "./tabs/tab-payment";
import {TabReviews} from "./tabs/tab-reviews";
import TabTopProducts from "./tabs/tab-top-products";
import GlowingMessage from "../../errors/glowing-message";

const DistributorDetails = ({navOpen, tenantType}) => {
    const {id} = useParams()
    const {t} = useTranslation()
    const [loading, setLoading] = useState(true)
    const [distributor, setDistributor] = useState(null)
    const [logoUrl, setLogoUrl] = useState(null)
    const [tabValue, setTabValue] = useState(0)
    const navigate = useNavigate()
    const [message, setMessage] = useState("")

    // Fetch distributor details
    useEffect(() => {
        const fetchDistributor = async () => {
            setLoading(true)
            try {
                const response = await axiosInstance.get(`/api/v1/tenants/${id}`, {
                    headers: {
                        "Language-Context": i18n.language.toUpperCase() || "EN",
                    },
                })
                setDistributor(response.data)

                // Fetch logo
                if (response.data.imageUrl) {
                    const logo = await GetLogo({filename: response.data.imageUrl})
                    setLogoUrl(logo)
                }
            } catch (error) {
                console.error("Error fetching distributor:", error)
                setMessage(error.response?.data?.message || "Failed to fetch distributor details.")
                // For demo purposes, set sample data
                const sampleData = {
                    id: id,
                    tenantNameEn: "Alfa Pharm",
                    imageUrl: "logo.png",
                    address: {country: "Armenia", city: "Yerevan", street: "123 Main Street"},
                    phones: [
                        {phoneNumberCode: "+374", phoneNumber: "10-123456"},
                        {phoneNumberCode: "+374", phoneNumber: "10-654321"},
                    ],
                    tenantEmail: "info@alfapharm.am",
                    website: "www.alfapharm.am",
                    description:
                        "Alfa Pharm is a leading pharmaceutical distributor in Armenia, providing high-quality medications and medical supplies to pharmacies and healthcare facilities across the country. With over 15 years of experience, we pride ourselves on reliability, competitive pricing, and excellent customer service.",
                    foundedYear: 2005,
                    category: "PHARMACY",
                    licenseNumber: "PH-12345-ARM",
                    paymentMethods: ["Credit Card", "Bank Transfer", "Cash on Delivery"],
                    deliveryOptions: ["Standard Delivery", "Express Delivery", "Pickup"],
                    businessHours: "Monday-Friday: 9:00-18:00, Saturday: 10:00-15:00",
                }
                setDistributor(sampleData)

                // Set placeholder logo
                setLogoUrl("/placeholder.svg?height=120&width=120")
            } finally {
                setLoading(false)
            }
        }

        fetchDistributor().then(r => console.log(r))
    }, [id, i18n.language])


    const handleTabChange = (event, newValue) => {
        setTabValue(newValue)
    }

    const handleBackClick = () => {
        navigate("/distributors")
    }

    // Format address as a string
    const addressString = distributor?.address
        ? `${distributor.address.street}, ${distributor.address.city}, ${distributor.address.country}`
        : ""

    // Format all phone numbers into a single string
    const phoneNumbersString =
        distributor?.phones && distributor.phones.length > 0
            ? distributor.phones.map((phone) => `${phone.phoneNumberCode} ${phone.phoneNumber}`).join(", ")
            : ""


    return (
        <>
            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <PageContainer navOpen={navOpen}>
                <Box sx={{display: "flex", alignItems: "center", mb: 3}}>
                    {tenantType !== "SYSTEM" && <IconButton onClick={handleBackClick} sx={{mr: 2}}>
                        <ArrowBackIcon/>
                    </IconButton>}
                    <CardHeader title={t("DistributorDetails")}/>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                        <CircularProgress/>
                    </Box>
                ) : distributor ? (
                    <>
                        {/* Distributor Header */}
                        <Paper elevation={2} sx={{mb: 3, overflow: "hidden"}}>
                            <Box
                                sx={{
                                    p: 3,
                                    background: "linear-gradient(to right, #f5f7fa, #e4e8f0)",
                                    display: "flex",
                                    flexDirection: {xs: "column", md: "row"},
                                    alignItems: {xs: "center", md: "flex-start"},
                                }}
                            >
                                <Avatar
                                    src={logoUrl}
                                    alt={distributor.tenantNameEn}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mr: {xs: 0, md: 4},
                                        mb: {xs: 2, md: 0},
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                    }}
                                />
                                <Box sx={{flex: 1}}>
                                    <Box sx={{display: "flex", alignItems: "center", flexWrap: "wrap"}}>
                                        <Typography variant="h4" component="h1" sx={{fontWeight: 500, mr: 1}}>
                                            {distributor.tenantNameEn}
                                        </Typography>
                                        <VerifiedIcon color="primary" sx={{fontSize: 24}}/>
                                        <Chip
                                            icon={<StorefrontIcon/>}
                                            label={t(distributor.category || "PHARMACY")}
                                            color="primary"
                                            sx={{ml: {xs: 0, sm: 2}, mt: {xs: 1, sm: 0}}}
                                        />
                                    </Box>

                                    <Grid container spacing={2} sx={{mt: 2}}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                <LocationOnIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                <Typography variant="body1">{addressString}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                <PhoneIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                <Typography variant="body1">{phoneNumbersString}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                <EmailIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                <Typography variant="body1">{distributor.tenantEmail}</Typography>
                                            </Box>
                                        </Grid>
                                        {distributor.website && (
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                    <LanguageIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                    <Typography
                                                        variant="body1"
                                                        component="a"
                                                        href={`https://${distributor.website}`}
                                                        target="_blank"
                                                        sx={{color: "primary.main", textDecoration: "none"}}
                                                    >
                                                        {distributor.website}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        {distributor.foundedYear && (
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                    <CalendarTodayIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                    <Typography variant="body1">
                                                        {t("Founded")}: {distributor.foundedYear} {t("Year")}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        {distributor.licenseNumber && (
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                    <BusinessIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}}/>
                                                    <Typography variant="body1">
                                                        {t("License")}: {distributor.licenseNumber}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Box>

                            {/* Tabs Navigation */}
                            <Box sx={{borderBottom: 1, borderColor: "divider"}}>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    aria-label="distributor tabs"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    <Tab label={t("Overview")} id="distributor-tab-0"
                                         aria-controls="distributor-tabpanel-0"/>
                                    <Tab label={t("Products")} id="distributor-tab-1"
                                         aria-controls="distributor-tabpanel-1"/>
                                    <Tab label={t("TopProducts")} id="distributor-tab-2"
                                         aria-controls="distributor-tabpanel-2"/>
                                    <Tab label={t("Delivery")} id="distributor-tab-3"
                                         aria-controls="distributor-tabpanel-3"/>
                                    <Tab label={t("Payment")} id="distributor-tab-4"
                                         aria-controls="distributor-tabpanel-4"/>
                                    <Tab label={t("Reviews")} id="distributor-tab-5"
                                         aria-controls="distributor-tabpanel-5"/>

                                </Tabs>
                            </Box>

                            {/* Tab Content */}
                            <Box sx={{p: 3}}>
                                {/* Overview Tab */}
                                <TabOverview tabValue={tabValue} distributor={distributor}/>

                                {/* Products Tab */}
                                <TabAllProducts tabValue={tabValue} tenantId={distributor.id} tenantType={tenantType}/>

                                {/* Top Products Tab */}
                                <TabTopProducts tabValue={tabValue} tenantId={distributor.id} tenantType={tenantType}/>

                                {/* Delivery Tab */}
                                <TabDelivery tabValue={tabValue} distributor={distributor}/>

                                {/* Payment Tab */}
                                <TabPayment tabValue={tabValue} distributor={distributor}/>

                                {/* Reviews Tab */}
                                <TabReviews tabValue={tabValue} distributor={distributor}/>

                            </Box>
                        </Paper>
                    </>
                ) : (
                    <Typography variant="h6" sx={{textAlign: "center", py: 4}}>
                        {t("NoDistributors")}
                    </Typography>
                )}
            </PageContainer>
        </>
    )
}

export default DistributorDetails
